/**
 * Division Management Page
 * CRUD interface for divisions following CRUD_PATTERNS.md
 *
 * Pattern: Same structure as UserManagementPage.tsx
 * - Uses usePagination, useColumnFilters, useTableSort, useDebounce hooks
 * - Uses useDivisionPageData for data fetching
 * - RZTable with full sorting, columnFilters, and pagination props
 *
 * #937: name, description and L1/L2/L3 leads are effective-dated. The list shows the
 * Current Version; a Future Version is one icon by the name whose tooltip lists its
 * changes (ADO-1159). Leads are edited only in the Edit modal, which saves
 * through one versioned PUT and says before saving whether it edits the Future Version
 * or creates the next one.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, Plus, Edit, Trash2, Power, PowerOff, Users, FileText } from 'lucide-react';
import { usePagination } from '@hooks/usePagination';
import { useColumnFilters } from '@hooks/useColumnFilters';
import { useTableSort } from '@hooks/useTableSort';
import { useDebounce } from '@hooks/useDebounce';
import PageLayout from '@components/layout/PageLayout';
import ListPageHeader from '@components/common/ListPageHeader';
import { RZTable, ColumnSelector } from '@components/common/table';
import { GenericForm, FormField } from '@components/common/forms';
import { GenericModal } from '@components/common/modals';
import ConfirmActionModal from '@components/admin/ConfirmActionModal';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import { FilterButtons, FilterOption } from '@components/common/FilterButtons';
import { useDivisionPageData } from '@hooks/useDivisionPageData';
import { divisionService } from '@services';
import { TableColumn, ApiError } from '@app-types';
import {
  Division,
  DivisionFilters,
  DivisionLead,
  DivisionLeadDraft,
  DivisionLeadInfo,
  DivisionUpdateData,
  DivisionVersionHistoryEntry
} from '@app-types/division';
import DivisionLeadsSelector from '@components/features/divisions/DivisionLeadsSelector';
import DivisionFutureVersionIndicator from '@components/features/divisions/DivisionFutureVersionIndicator';
import DivisionVersionHistory from '@components/features/divisions/DivisionVersionHistory';
import type { LeadUserInfo } from '@components/features/divisions/DivisionLeadsSelector';
import { DivisionNameCell, DivisionLeadsCell, DivisionCountCell } from '@components/common/table';
import Message from '@components/common/Message';
import { getApiErrorMessage, getApiErrorStatus, splitApiFieldErrors } from '@utils/apiError';
import { addDaysToDate, toLocalDateInput } from '@utils/dateUtils';
import {
  defaultEffectiveDate,
  describeSaveOutcome,
  latestVersion,
  NEW_VERSION_EFFECTIVE_OFFSET_DAYS
} from '@utils/divisionVersionUtils';
import styles from './DivisionManagementPage.module.css';

// Filter options for status filtering
const statusFilters: FilterOption[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' }
];

/** Column-visibility storage key. Bumped when the column set changed (L1/L2/L3 replaced "Division Leads"). */
const VISIBLE_COLUMNS_STORAGE_KEY = 'division-visible-columns-v2';

const toLeadDraft = (lead: DivisionLeadInfo | DivisionLead): DivisionLeadDraft => ({
  user_id: lead.user_id,
  user_name: lead.user_name,
  user_email: lead.user_email
});

const sameLeadIds = (a: DivisionLeadDraft[], b: DivisionLeadDraft[]): boolean => {
  if (a.length !== b.length) return false;
  const ids = new Set(a.map((l) => l.user_id));
  return b.every((l) => ids.has(l.user_id));
};

type SaveErrorKind = 'lead-not-found' | 'validation' | 'conflict' | 'other';

interface SaveError {
  kind: SaveErrorKind;
  message: string;
}

/**
 * Turn a create / save failure into a banner message. A nonexistent lead user id (404) and a
 * lead that is inactive or not an Executive (400) read differently (#936 decision 4).
 */
const describeSaveError = (error: unknown, fallback: string): SaveError => {
  const status = getApiErrorStatus(error);
  const serverMessage = getApiErrorMessage(error, '');
  if (status === 404) {
    return {
      kind: 'lead-not-found',
      message: `A selected lead no longer exists. Remove them and pick another user.${serverMessage ? ` (${serverMessage})` : ''}`
    };
  }
  if (status === 400) {
    return {
      kind: 'validation',
      message: serverMessage || 'A selected lead is not an active Executive. Pick an active Executive user.'
    };
  }
  if (status === 409) {
    return {
      kind: 'conflict',
      message: serverMessage || 'This change conflicts with another division or version. Reload and try again.'
    };
  }
  return { kind: 'other', message: serverMessage || fallback };
};

const DIVISION_FORM_FIELDS = ['name', 'description', 'effective_date', 'note'] as const;

/** Map Joi details onto form fields; keep the banner for lead/conflict/unmapped errors. */
const applyDivisionSaveFailure = (
  error: unknown,
  fallback: string,
  setBanner: (error: SaveError | null) => void,
  setFieldErrors: (errors: Record<string, string>) => void
): void => {
  const { fieldErrors, residualMessages } = splitApiFieldErrors(error, DIVISION_FORM_FIELDS);
  setFieldErrors(fieldErrors);
  const status = getApiErrorStatus(error);
  if (Object.keys(fieldErrors).length > 0 && residualMessages.length === 0 && status === 400) {
    setBanner(null);
    return;
  }
  setBanner(describeSaveError(error, fallback));
};

// Column configuration with filter keys for backend
const divisionColumns: TableColumn[] = [
  { key: 'division_code', label: 'ID', defaultVisible: true, sortable: false, filterable: false },
  { key: 'version', label: 'Version', defaultVisible: true, centered: true, sortable: false, filterable: false },
  { key: 'name', label: 'Division Name', defaultVisible: true, sortable: true, filterKey: 'name' },
  { key: 'description', label: 'Description', defaultVisible: true, sortable: false, filterKey: 'description' },
  { key: 'lead_l1', label: 'Lead L1', defaultVisible: true, sortable: false, filterable: false },
  { key: 'lead_l2', label: 'Lead L2', defaultVisible: true, sortable: false, filterable: false },
  { key: 'lead_l3', label: 'Lead L3', defaultVisible: true, sortable: false, filterable: false },
  { key: 'user_count', label: 'Users', defaultVisible: true, centered: true, sortable: true, filterable: false },
  {
    key: 'contract_count',
    label: 'Contracts',
    defaultVisible: true,
    centered: true,
    sortable: true,
    filterable: false
  },
  { key: 'status', label: 'Status', defaultVisible: true, centered: true, sortable: false, filterable: false },
  {
    key: 'actions',
    label: 'Actions',
    defaultVisible: true,
    centered: true,
    sortable: false,
    alwaysVisible: true,
    filterable: false
  }
];

const DivisionManagementPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // State from URL parameters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'active');

  // Pagination with localStorage persistence
  const { currentPage, pageSize, setCurrentPage, setPageSize, resetToFirstPage } = usePagination({
    storageKey: 'divisions-page-size',
    defaultPageSize: 25
  });

  // Debounced search
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Column filters with URL persistence
  const {
    filterValues: columnFilterValues,
    debouncedFilterValues: debouncedColumnFilters,
    setFilter: setColumnFilter,
    clearAllFilters: clearColumnFilters,
    hasActiveFilters: hasActiveColumnFilters,
    getApiParam: getColumnFiltersParam
  } = useColumnFilters({ columns: divisionColumns });

  // Sorting with hook
  const { sortConfig, handleSort, isSorted } = useTableSort({
    columnMapping: {
      name: 'name',
      user_count: 'user_count',
      contract_count: 'contract_count'
    },
    defaultSort: { field: 'name', direction: 'ASC' },
    onPageReset: () => setCurrentPage(1)
  });

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [selectedDivision, setSelectedDivision] = useState<Division | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Create modal: L1/L2/L3 picks live outside GenericForm (it has no bounded user-picker field)
  const [createL1, setCreateL1] = useState<DivisionLeadDraft | null>(null);
  const [createL2, setCreateL2] = useState<DivisionLeadDraft[]>([]);
  const [createL3, setCreateL3] = useState<DivisionLeadDraft[]>([]);
  const [createError, setCreateError] = useState<SaveError | null>(null);
  const [createFieldErrors, setCreateFieldErrors] = useState<Record<string, string>>({});

  // Edit modal: level drafts of the most recent version (local-draft pattern), saved with the
  // name, description, Effective Date and note in one versioned PUT (#937)
  const [draftL1, setDraftL1] = useState<DivisionLeadDraft | null>(null);
  const [draftL2, setDraftL2] = useState<DivisionLeadDraft[]>([]);
  const [draftL3, setDraftL3] = useState<DivisionLeadDraft[]>([]);
  const originalL1Ref = useRef<DivisionLeadDraft | null>(null);
  const originalL2Ref = useRef<DivisionLeadDraft[]>([]);
  const originalL3Ref = useRef<DivisionLeadDraft[]>([]);
  const [editEffectiveDate, setEditEffectiveDate] = useState('');
  const [editError, setEditError] = useState<SaveError | null>(null);
  const [editFieldErrors, setEditFieldErrors] = useState<Record<string, string>>({});

  // Edit modal: read-only Version History (#1163), loaded beside the division so it never holds up editing
  const [versionHistory, setVersionHistory] = useState<DivisionVersionHistoryEntry[]>([]);
  const [isVersionHistoryLoading, setIsVersionHistoryLoading] = useState(false);
  const [versionHistoryError, setVersionHistoryError] = useState<string | null>(null);
  // A response for an earlier open must not overwrite the history of the division open now
  const versionHistoryRequestRef = useRef(0);

  // Form ref for Edit Division modal (external submit trigger)
  const editFormRef = useRef<HTMLFormElement>(null);

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    const stored = localStorage.getItem(VISIBLE_COLUMNS_STORAGE_KEY);
    if (stored) return new Set(JSON.parse(stored));
    return new Set(divisionColumns.filter((col) => col.defaultVisible).map((col) => col.key));
  });

  // Save column preferences
  useEffect(() => {
    localStorage.setItem(VISIBLE_COLUMNS_STORAGE_KEY, JSON.stringify(Array.from(visibleColumns)));
  }, [visibleColumns]);

  // Update URL when filters change.
  // Clone existing params so cf_* column filters (and page/limit) are preserved.
  // Pagination is owned by usePagination; do not rewrite page here.
  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    if (debouncedSearchQuery) {
      params.set('search', debouncedSearchQuery);
    } else {
      params.delete('search');
    }

    if (statusFilter !== 'active') {
      params.set('status', statusFilter);
    } else {
      params.delete('status');
    }

    setSearchParams(params, { replace: true });
  }, [debouncedSearchQuery, statusFilter, searchParams, setSearchParams]);

  // Main data fetch with filters using hook
  const {
    divisions,
    isLoading,
    isFetching,
    filterCounts,
    totalItems: totalDivisions,
    totalPages,
    refreshData
  } = useDivisionPageData({
    queryKey: [
      'divisions',
      debouncedSearchQuery,
      statusFilter,
      currentPage,
      pageSize,
      debouncedColumnFilters,
      sortConfig
    ],
    queryFn: () => {
      const filters: DivisionFilters = {
        search: debouncedSearchQuery || undefined,
        columnFilters: getColumnFiltersParam() || undefined,
        sortBy: sortConfig.field,
        order: sortConfig.direction
      };

      // Map statusFilter to API status parameter
      if (statusFilter !== 'all') {
        filters.status = statusFilter as 'active' | 'inactive';
      } else {
        filters.status = 'all';
      }

      return divisionService.getAll(filters, currentPage, pageSize);
    }
  });

  // Form fields
  const createFormFields: FormField[] = [
    { name: 'name', label: 'Division Name', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'text', required: false },
    { name: 'version', label: 'Version', type: 'readonly' },
    { name: 'effective_date', label: 'Effective Date', type: 'readonly' },
    { name: 'note', label: 'Note', type: 'text', required: false, placeholder: 'Optional note stored with the division' }
  ];

  // Display-only defaults; the server sets both and ignores any value sent (never included in the body).
  // GenericForm reads initialData on mount, and the modal unmounts on close, so each open is fresh.
  const today = toLocalDateInput();
  const earliestEffectiveDate = useMemo(() => addDaysToDate(today, 1), [today]);
  const createInitialData = { version: '1', effective_date: addDaysToDate(today, NEW_VERSION_EFFECTIVE_OFFSET_DAYS) };

  const createLeadsIncomplete = !createL1 || createL2.length < 1 || createL3.length < 1;

  const resetCreateState = () => {
    setCreateL1(null);
    setCreateL2([]);
    setCreateL3([]);
    setCreateError(null);
    setCreateFieldErrors({});
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    resetCreateState();
  };

  const editFormFields: FormField[] = [
    { name: 'name', label: 'Division Name', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'text', required: false },
    // The server accepts only a date after today (its own clock decides).
    { name: 'effective_date', label: 'Effective Date', type: 'date', required: true, min: earliestEffectiveDate },
    { name: 'note', label: 'Note', type: 'text', required: false, placeholder: 'Optional note stored with the change' }
  ];

  const editLatest = selectedDivision ? latestVersion(selectedDivision) : null;
  const editInitialEffectiveDate = selectedDivision ? defaultEffectiveDate(selectedDivision, today) : '';

  // Handlers
  const handleCreate = async (data: Record<string, string | string[] | boolean>) => {
    if (!createL1 || createL2.length < 1 || createL3.length < 1) return;
    setIsSubmitting(true);
    setCreateError(null);
    setCreateFieldErrors({});
    try {
      const note = typeof data.note === 'string' ? data.note.trim() : '';
      const description = typeof data.description === 'string' ? data.description : '';
      await divisionService.create({
        name: data.name as string,
        l1_user_id: createL1.user_id,
        l2_user_ids: createL2.map((l) => l.user_id),
        l3_user_ids: createL3.map((l) => l.user_id),
        description: description || undefined,
        note: note || undefined
      });
      refreshData();
      closeCreateModal();
    } catch (error) {
      console.error('Create failed', error);
      // Keep the form open with the user's input; show why the server refused it.
      applyDivisionSaveFailure(error, 'Failed to create division', setCreateError, setCreateFieldErrors);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (data: Record<string, string | string[] | boolean>) => {
    if (!selectedDivision || !editLatest) return;
    if (editLeadsBlockSave) return;
    setIsSubmitting(true);
    setEditError(null);
    setEditFieldErrors({});
    try {
      // Only what changed against the most recent version; the server copies the rest.
      const changes: DivisionUpdateData = {};
      const name = typeof data.name === 'string' ? data.name : '';
      const description = typeof data.description === 'string' ? data.description : '';
      const effectiveDate = typeof data.effective_date === 'string' ? data.effective_date : '';
      const note = typeof data.note === 'string' ? data.note.trim() : '';
      if (name !== editLatest.name) changes.name = name;
      if (description !== (editLatest.description || '')) changes.description = description;
      if (hasLeadChanges && draftL1) {
        changes.l1_user_id = draftL1.user_id;
        changes.l2_user_ids = draftL2.map((l) => l.user_id);
        changes.l3_user_ids = draftL3.map((l) => l.user_id);
      }
      const hasVersionChanges = Object.keys(changes).length > 0 || effectiveDate !== editInitialEffectiveDate;
      if (note && !hasVersionChanges) {
        // A note is saved with a change; on its own it would only add an identical version.
        setEditError({ kind: 'validation', message: 'A note is saved together with a change. Change a field or the Effective Date too.' });
        return;
      }
      if (hasVersionChanges) {
        changes.effective_date = effectiveDate;
        if (note) changes.note = note;
        await divisionService.update(selectedDivision.id, changes);
      }

      refreshData();
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Update failed', error);
      // Keep the form open with the user's input; show why the server refused it.
      applyDivisionSaveFailure(error, 'Failed to update division', setEditError, setEditFieldErrors);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!selectedDivision) return;
    setIsSubmitting(true);
    try {
      await divisionService.deactivate(selectedDivision.id);
      refreshData();
      setIsDeactivateModalOpen(false);
      setSelectedDivision(null);
    } catch (error) {
      const apiError = error as ApiError;
      console.error('Deactivate failed', error);
      alert(apiError.response?.data?.error || 'Failed to deactivate division');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReactivate = useCallback(
    async (division: Division) => {
      setIsSubmitting(true);
      try {
        await divisionService.reactivate(division.id);
        refreshData();
      } catch (error) {
        const apiError = error as ApiError;
        console.error('Reactivate failed', error);
        alert(apiError.response?.data?.error || 'Failed to reactivate division');
      } finally {
        setIsSubmitting(false);
      }
    },
    [refreshData]
  );

  const handleDelete = async () => {
    if (!selectedDivision) return;
    setIsSubmitting(true);
    try {
      await divisionService.deleteDivision(selectedDivision.id);
      refreshData();
      setIsDeleteModalOpen(false);
      setSelectedDivision(null);
    } catch (error) {
      const apiError = error as ApiError;
      console.error('Delete failed', error);
      alert(apiError.response?.data?.error || 'Failed to delete division');
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Draft-based lead management (local-draft pattern) ---

  const initLeadDrafts = useCallback((division: Division) => {
    const { leads } = latestVersion(division);
    const l1 = leads.l1 ? toLeadDraft(leads.l1) : null;
    const l2 = leads.l2.map(toLeadDraft);
    const l3 = leads.l3.map(toLeadDraft);
    setDraftL1(l1);
    setDraftL2(l2);
    setDraftL3(l3);
    originalL1Ref.current = l1;
    originalL2Ref.current = l2;
    originalL3Ref.current = l3;
    setEditError(null);
    setEditFieldErrors({});
  }, []);

  /** Adapters from the selector's add/remove callbacks onto a single (L1) or list (L2/L3) draft. */
  const singleLeadHandlers = (set: React.Dispatch<React.SetStateAction<DivisionLeadDraft | null>>) => ({
    onAddLead: (userId: number, userInfo?: LeadUserInfo) => {
      if (!userInfo) return;
      set({ user_id: userId, user_name: userInfo.name, user_email: userInfo.email });
    },
    onRemoveLead: () => set(null)
  });

  const multiLeadHandlers = (set: React.Dispatch<React.SetStateAction<DivisionLeadDraft[]>>) => ({
    onAddLead: (userId: number, userInfo?: LeadUserInfo) => {
      if (!userInfo) return;
      set((prev) =>
        prev.some((l) => l.user_id === userId)
          ? prev
          : [...prev, { user_id: userId, user_name: userInfo.name, user_email: userInfo.email }]
      );
    },
    onRemoveLead: (userId: number) => set((prev) => prev.filter((l) => l.user_id !== userId))
  });

  const hasLeadChanges = useMemo(() => {
    const originalL1 = originalL1Ref.current;
    if ((originalL1?.user_id ?? null) !== (draftL1?.user_id ?? null)) return true;
    return !sameLeadIds(originalL2Ref.current, draftL2) || !sameLeadIds(originalL3Ref.current, draftL3);
  }, [draftL1, draftL2, draftL3]);

  const leadDraftsIncomplete = !draftL1 || draftL2.length < 1 || draftL3.length < 1;
  // The server checks the full lead set only when a save sends leads, so a division
  // migrated without leads can still be renamed; once leads are touched, all three levels are needed.
  const editLeadsBlockSave = hasLeadChanges && leadDraftsIncomplete;

  const loadVersionHistory = useCallback(async (divisionId: number) => {
    const requestId = ++versionHistoryRequestRef.current;
    setVersionHistory([]);
    setVersionHistoryError(null);
    setIsVersionHistoryLoading(true);
    try {
      const versions = await divisionService.getVersions(divisionId);
      if (requestId !== versionHistoryRequestRef.current) return;
      setVersionHistory(versions);
    } catch (error) {
      if (requestId !== versionHistoryRequestRef.current) return;
      console.error('Failed to fetch division version history', error);
      setVersionHistoryError('Failed to load version history');
    } finally {
      if (requestId === versionHistoryRequestRef.current) setIsVersionHistoryLoading(false);
    }
  }, []);

  const openEditModal = useCallback(
    async (divisionId: number) => {
      // Fetched on every open, so a version saved last time is listed next time
      loadVersionHistory(divisionId);
      try {
        const fullDivision = await divisionService.getById(divisionId);
        setSelectedDivision(fullDivision);
        initLeadDrafts(fullDivision);
        setEditEffectiveDate(defaultEffectiveDate(fullDivision, toLocalDateInput()));
        setIsEditModalOpen(true);
      } catch (error) {
        console.error('Failed to fetch division details', error);
        alert('Failed to load division details');
      }
    },
    [initLeadDrafts, loadVersionHistory]
  );

  // Cell renderer
  const renderCell = useCallback(
    (column: TableColumn, item: Division) => {
      switch (column.key) {
        case 'division_code':
          return <span data-test-id="division-code">{item.division_code}</span>;

        case 'version':
          return <span data-test-id="division-version">{item.current_version.version}</span>;

        case 'name':
          return (
            <DivisionNameCell
              name={item.name}
              suffix={
                item.future_version && (
                  <DivisionFutureVersionIndicator current={item.current_version} future={item.future_version} />
                )
              }
            />
          );

        case 'description':
          return item.description || <span className={styles.textMuted}>-</span>;

        case 'lead_l1': {
          const { l1 } = item.current_version.leads;
          return (
            <DivisionLeadsCell
              leads={l1 ? [l1] : []}
              emptyText="No L1 lead"
              namesTestId="division-lead-l1"
              className={styles.levelLeadsCell}
            />
          );
        }

        case 'lead_l2':
          return (
            <DivisionLeadsCell
              leads={item.current_version.leads.l2}
              emptyText="No L2 leads"
              namesTestId="division-lead-l2"
              className={styles.levelLeadsCell}
            />
          );

        case 'lead_l3':
          return (
            <DivisionLeadsCell
              leads={item.current_version.leads.l3}
              emptyText="No L3 leads"
              namesTestId="division-lead-l3"
              className={styles.levelLeadsCell}
            />
          );

        case 'user_count':
          return <DivisionCountCell count={item.user_count} icon={<Users size={14} />} />;

        case 'contract_count':
          return <DivisionCountCell count={item.contract_count} icon={<FileText size={14} />} />;

        case 'status':
          return (
            <Badge variant={item.is_active ? 'success' : 'default'}>{item.is_active ? 'Active' : 'Inactive'}</Badge>
          );

        case 'actions':
          return (
            <div className={styles.actionButtons}>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openEditModal(item.id)}
                title="Edit"
                className="hover:bg-blue-50"
                data-test-id="action-button-edit"
              >
                <Edit size={16} color="#2563eb" />
              </Button>

              {item.is_active ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSelectedDivision(item);
                    setIsDeactivateModalOpen(true);
                  }}
                  title="Deactivate"
                  className="hover:bg-orange-50"
                  data-test-id="action-button-deactivate"
                >
                  <PowerOff size={16} color="#d97706" />
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleReactivate(item)}
                  title="Reactivate"
                  className="hover:bg-green-50"
                  data-test-id="action-button-activate"
                >
                  <Power size={16} color="#059669" />
                </Button>
              )}

              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelectedDivision(item);
                  setIsDeleteModalOpen(true);
                }}
                title="Delete"
                disabled={item.user_count > 0 || item.contract_count > 0}
                className="hover:bg-red-50"
                data-test-id="action-button-delete"
              >
                <Trash2 size={16} color={item.user_count > 0 || item.contract_count > 0 ? '#d1d5db' : '#dc2626'} />
              </Button>
            </div>
          );

        default: {
          const value = item[column.key as keyof Division];
          // Handle primitive values only
          if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
          }
          return null;
        }
      }
    },
    [handleReactivate, openEditModal]
  );

  // Handler functions
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    resetToFirstPage();
  };

  const handleFilterChange = (filter: string) => {
    setStatusFilter(filter);
    resetToFirstPage();
  };

  const toggleColumn = (columnKey: string) => {
    const column = divisionColumns.find((col) => col.key === columnKey);
    if (column?.alwaysVisible) return;

    setVisibleColumns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  return (
    <PageLayout title="Division Management" showHeader={false}>
      <ListPageHeader
        icon={<Building2 size={24} />}
        title="Division Management"
        count={filterCounts.all}
        searchValue={searchQuery}
        onSearchChange={handleSearch}
        searchPlaceholder="Search divisions..."
        actions={
          <ColumnSelector
            columns={divisionColumns}
            visibleColumns={Array.from(visibleColumns)}
            onToggleColumn={toggleColumn}
          />
        }
        additionalContent={
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              resetCreateState();
              setIsCreateModalOpen(true);
            }}
          >
            <Plus size={16} />
            Add Division
          </Button>
        }
      />

      <div className={styles.filters}>
        <FilterButtons
          filters={statusFilters}
          activeFilter={statusFilter}
          onFilterChange={handleFilterChange}
          filterCounts={filterCounts}
          dataTestId="divisions-filter"
        />
      </div>

      {/* Loading overlay when fetching data */}
      {isFetching && !isLoading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
        </div>
      )}

      <RZTable
        data={divisions}
        columns={divisionColumns.filter((col) => visibleColumns.has(col.key))}
        getRowKey={(division) => division.id}
        renderCell={renderCell}
        sorting={{
          sortConfig,
          onSort: handleSort,
          isSorted
        }}
        columnFilters={{
          values: columnFilterValues,
          onChange: setColumnFilter,
          onClearAll: clearColumnFilters,
          hasActiveFilters: hasActiveColumnFilters
        }}
        pagination={{
          currentPage,
          totalPages,
          totalItems: totalDivisions,
          pageSize,
          onPageChange: setCurrentPage,
          onPageSizeChange: setPageSize,
          pageSizeOptions: [25, 50, 100],
          itemName: 'divisions'
        }}
        state={{
          isLoading,
          isEmpty: divisions.length === 0,
          emptyTitle: 'No divisions found',
          emptyDescription:
            statusFilter === 'active'
              ? 'Create your first division to get started'
              : 'No divisions match the current filters',
          emptyType: searchQuery ? 'search' : 'default',
          loadingMessage: 'Loading divisions...'
        }}
        rowClassName={(item) => (!item.is_active ? styles.inactiveRow : '')}
      />

      {/* Create Modal */}
      <GenericModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        title="Create New Division"
        disableBackdropClick
        dataTestId="create-division-modal"
      >
        {createError && (
          <Message
            variant="error"
            className={styles.formError}
            data-test-id="create-division-error"
            data-error-kind={createError.kind}
            role="alert"
          >
            <span>{createError.message}</span>
          </Message>
        )}
        <GenericForm
          fields={createFormFields}
          initialData={createInitialData}
          onSubmit={handleCreate}
          onCancel={closeCreateModal}
          isSubmitting={isSubmitting}
          submitLabel="Create Division"
          submitDisabled={createLeadsIncomplete}
          externalErrors={createFieldErrors}
        >
          <div className={styles.modalSection}>
            <h4>
              Division Lead L1 <span className={styles.required}>*</span>
            </h4>
            <DivisionLeadsSelector
              selectedLeads={createL1 ? [createL1] : []}
              {...singleLeadHandlers(setCreateL1)}
              mode="single"
              minSelections={1}
              showStatus
              emptyText="No L1 lead selected"
              searchPlaceholder="Search Executives to set as L1 lead..."
              dataTestId="create-lead-l1"
            />
          </div>
          <div className={styles.modalSection}>
            <h4>
              Division Lead L2 <span className={styles.required}>*</span>
            </h4>
            <DivisionLeadsSelector
              selectedLeads={createL2}
              {...multiLeadHandlers(setCreateL2)}
              minSelections={1}
              showStatus
              emptyText="No L2 leads selected"
              searchPlaceholder="Search Executives to add as L2 lead..."
              dataTestId="create-lead-l2"
            />
          </div>
          <div className={styles.modalSection}>
            <h4>
              Division Lead L3 <span className={styles.required}>*</span>
            </h4>
            <DivisionLeadsSelector
              selectedLeads={createL3}
              {...multiLeadHandlers(setCreateL3)}
              minSelections={1}
              showStatus
              emptyText="No L3 leads selected"
              searchPlaceholder="Search Executives to add as L3 lead..."
              dataTestId="create-lead-l3"
            />
          </div>
          {createLeadsIncomplete && (
            <p className={styles.leadHint} data-test-id="create-division-leads-hint">
              Choose one L1 lead and at least one L2 and one L3 lead. Leads must be active Executives.
            </p>
          )}
        </GenericForm>
      </GenericModal>

      {/* Edit Modal */}
      <GenericModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title={`Edit Division - ${selectedDivision?.name || ''}`}
        disableBackdropClick
        dataTestId="edit-division-modal"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsEditModalOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                editFormRef.current?.requestSubmit();
              }}
              loading={isSubmitting}
              disabled={isSubmitting || editLeadsBlockSave || !selectedDivision?.is_active}
              data-test-id="edit-division-save"
            >
              Save Changes
            </Button>
          </>
        }
      >
        {selectedDivision && editLatest && (
          <>
            {!selectedDivision.is_active && (
              <Message variant="warning" className={styles.formError} data-test-id="edit-division-inactive">
                <span>This division is deactivated. Reactivate it before changing it.</span>
              </Message>
            )}
            {editError && (
              <Message
                variant="error"
                className={styles.formError}
                data-test-id="edit-division-error"
                data-error-kind={editError.kind}
                role="alert"
              >
                <span>{editError.message}</span>
              </Message>
            )}
            <GenericForm
              fields={editFormFields}
              initialData={{
                name: editLatest.name,
                description: editLatest.description || '',
                effective_date: editInitialEffectiveDate,
                note: ''
              }}
              onSubmit={handleUpdate}
              onCancel={() => setIsEditModalOpen(false)}
              onFieldChange={(name, value) => {
                if (name === 'effective_date' && typeof value === 'string' && value !== '') {
                  setEditEffectiveDate(value);
                }
              }}
              isSubmitting={isSubmitting}
              submitLabel="Save Changes"
              hideActions
              formRef={editFormRef}
              externalErrors={editFieldErrors}
            />

            {/* What saving does, right under the Effective Date that decides it (#937) */}
            {editEffectiveDate && (
              <Message variant="info" className={styles.saveOutcome} data-test-id="edit-division-outcome">
                <span>{describeSaveOutcome(selectedDivision, editEffectiveDate, today)}</span>
              </Message>
            )}

            <div className={styles.modalSection}>
              <h4>
                Division Lead L1 <span className={styles.required}>*</span>
              </h4>
              <DivisionLeadsSelector
                selectedLeads={draftL1 ? [draftL1] : []}
                {...singleLeadHandlers(setDraftL1)}
                mode="single"
                minSelections={1}
                showStatus
                emptyText="No L1 lead assigned"
                searchPlaceholder="Search Executives to set as L1 lead..."
                dataTestId="edit-lead-l1"
              />
            </div>
            <div className={styles.modalSection}>
              <h4>
                Division Lead L2 <span className={styles.required}>*</span>
              </h4>
              <DivisionLeadsSelector
                selectedLeads={draftL2}
                {...multiLeadHandlers(setDraftL2)}
                minSelections={1}
                showStatus
                emptyText="No L2 leads assigned"
                searchPlaceholder="Search Executives to add as L2 lead..."
                dataTestId="edit-lead-l2"
              />
            </div>
            <div className={styles.modalSection}>
              <h4>
                Division Lead L3 <span className={styles.required}>*</span>
              </h4>
              <DivisionLeadsSelector
                selectedLeads={draftL3}
                {...multiLeadHandlers(setDraftL3)}
                minSelections={1}
                showStatus
                emptyText="No L3 leads assigned"
                searchPlaceholder="Search Executives to add as L3 lead..."
                dataTestId="edit-lead-l3"
              />
            </div>
            {editLeadsBlockSave && (
              <p className={styles.leadHint} data-test-id="edit-division-leads-hint">
                Choose one L1 lead and at least one L2 and one L3 lead. Leads must be active Executives.
              </p>
            )}
            <div className={styles.modalSection}>
              <DivisionVersionHistory
                versions={versionHistory}
                isLoading={isVersionHistoryLoading}
                error={versionHistoryError}
              />
            </div>
          </>
        )}
      </GenericModal>

      {/* Deactivate Confirmation */}
      <ConfirmActionModal
        isOpen={isDeactivateModalOpen}
        onClose={() => setIsDeactivateModalOpen(false)}
        onConfirm={handleDeactivate}
        title="Deactivate Division"
        message={`Are you sure you want to deactivate "${selectedDivision?.name}"? This division will no longer appear in dropdown selectors. Users currently in this division will NOT be automatically reassigned.`}
        isSubmitting={isSubmitting}
        actionType="deactivate"
      />

      {/* Delete Confirmation */}
      <ConfirmActionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Division"
        message={
          selectedDivision && (selectedDivision.user_count > 0 || selectedDivision.contract_count > 0)
            ? `Cannot delete division with ${selectedDivision.user_count} user(s) and ${selectedDivision.contract_count} contract(s). Please reassign or remove all users and contracts first.`
            : `Are you sure you want to permanently delete "${selectedDivision?.name}"? This action cannot be undone.`
        }
        isSubmitting={isSubmitting}
        actionType="delete"
        dependencies={
          selectedDivision
            ? {
                contracts_managed: selectedDivision.contract_count,
                active_assessments: selectedDivision.user_count
              }
            : undefined
        }
      />
    </PageLayout>
  );
};

export default DivisionManagementPage;
